import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Loader2, Download, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Invoice {
  id: string
  number: string | null
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | null
  amountPaidCents: number
  amountDueCents: number
  currency: string
  created: number
  periodStart: number
  periodEnd: number
  invoicePdfUrl: string | null
  hostedInvoiceUrl: string | null
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

function formatDate(epoch: number): string {
  return new Date(epoch * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const STATUS_STYLES: Record<string, string> = {
  paid: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  open: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  draft: 'bg-muted text-muted-foreground border-border',
  void: 'bg-muted text-muted-foreground border-border',
  uncollectible: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
}

export function InvoicesList() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  async function load(startingAfter?: string) {
    const isFirstLoad = !startingAfter
    if (isFirstLoad) setLoading(true)
    else setLoadingMore(true)

    try {
      const res = await api.get('/api/billing/invoices', {
        params: { limit: 20, startingAfter },
      })
      const newInvoices: Invoice[] = res.data.data.invoices
      if (isFirstLoad) setInvoices(newInvoices)
      else setInvoices((prev) => [...prev, ...newInvoices])
      setHasMore(res.data.data.hasMore)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.message ?? 'Failed to load invoices')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <div className="flex items-center py-6 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading invoices…
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    )
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No invoices yet. Your first invoice will appear here after your trial converts.
      </div>
    )
  }

  return (
    <div>
      <div className="rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <th className="text-left px-4 py-2.5 font-medium">Date</th>
              <th className="text-left px-4 py-2.5 font-medium">Invoice</th>
              <th className="text-left px-4 py-2.5 font-medium">Status</th>
              <th className="text-right px-4 py-2.5 font-medium">Amount</th>
              <th className="text-right px-4 py-2.5 font-medium">PDF</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, idx) => {
              const statusKey = (inv.status ?? 'draft') as keyof typeof STATUS_STYLES
              const amountToShow = inv.status === 'paid' ? inv.amountPaidCents : inv.amountDueCents
              return (
                <tr
                  key={inv.id}
                  className={`border-t ${idx % 2 === 1 ? 'bg-muted/20' : ''} hover:bg-accent/30 transition-colors`}
                >
                  <td className="px-4 py-3">{formatDate(inv.created)}</td>
                  <td className="px-4 py-3 font-mono text-[12px]">
                    {inv.number ?? inv.id.slice(-10)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        STATUS_STYLES[statusKey] ?? STATUS_STYLES.draft
                      }`}
                    >
                      {inv.status ?? 'draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">
                    {formatMoney(amountToShow, inv.currency)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {inv.invoicePdfUrl ? (
                      <a
                        href={inv.invoicePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand-royal hover:text-brand-royal/80 transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" />
                        PDF
                      </a>
                    ) : inv.hostedInvoiceUrl ? (
                      <a
                        href={inv.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand-royal hover:text-brand-royal/80 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>

      {hasMore && (
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            disabled={loadingMore}
            onClick={() => load(invoices[invoices.length - 1]?.id)}
          >
            {loadingMore ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                Loading…
              </>
            ) : (
              'Load older invoices'
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
